import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableRow,
  TableCell, TextField, Alert, CircularProgress, IconButton, Divider
} from '@mui/material';
import { Home, Print, Assessment, Description } from '@mui/icons-material';
import axios from 'axios';
import { fmt, sym } from '../utils/currency';
import CompanyHeader from './CompanyHeader';
import { useCompany } from '../hooks/useCompany';
import { downloadCsv } from '../utils/csvExport';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const firstOfYear = () => `${new Date().getFullYear()}-01-01`;
const todayStr    = () => new Date().toISOString().split('T')[0];

const Section = ({ title, rows, total, color }) => (
  <>
    <TableRow>
      <TableCell colSpan={2} sx={{ fontWeight: 800, color, pt: 2 }}>{title}</TableCell>
    </TableRow>
    {rows.length === 0 ? (
      <TableRow><TableCell colSpan={2}><Typography variant="body2" color="text.secondary" sx={{ pl: 2 }}>None</Typography></TableCell></TableRow>
    ) : rows.map(r => (
      <TableRow key={r.accountNo} hover>
        <TableCell sx={{ pl: 3 }}>{r.accountNo} — {r.accountName}</TableCell>
        <TableCell align="right">{fmt(r.amount)}</TableCell>
      </TableRow>
    ))}
    <TableRow>
      <TableCell sx={{ fontWeight: 700 }}>Total {title}</TableCell>
      <TableCell align="right" sx={{ fontWeight: 700, borderTop: '1px solid #ccc' }}>{sym()} {fmt(total)}</TableCell>
    </TableRow>
  </>
);

const IncomeStatement = () => {
  const navigate = useNavigate();
  const company = useCompany();
  const [from, setFrom]       = useState(firstOfYear());
  const [to, setTo]           = useState(todayStr());
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const run = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const { data } = await axios.get(`${API}/api/finance/reports/income-statement`, { headers: hdrs(), params: { from, to } });
      setData(data);
    } catch (err) { setError(err.response?.data?.error || 'Failed to load income statement'); }
    finally { setLoading(false); }
  }, [from, to]);

  useEffect(() => { run(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const profit = data ? data.totals.netProfit >= 0 : true;

  const exportExcel = () => {
    if (!data) return;
    const rows = [
      [company?.name || 'INCOME STATEMENT'],
      ['INCOME STATEMENT'],
      [`Period: ${from} to ${to}`],
      [`Generated: ${new Date().toLocaleDateString()}`],
      [],
      ['Account', 'Amount'],
      [], ['INCOME'],
      ...data.revenue.map(r => [`${r.accountNo} — ${r.accountName}`, r.amount]),
      ['Total Income', data.totals.income],
      [], ['EXPENSES'],
      ...data.expense.map(r => [`${r.accountNo} — ${r.accountName}`, r.amount]),
      ['Total Expenses', data.totals.expense],
      [],
      [data.totals.netProfit >= 0 ? 'NET PROFIT' : 'NET LOSS', data.totals.netProfit],
    ];
    downloadCsv(rows, `income_statement_${from}_to_${to}.csv`);
  };

  return (
    <Box sx={{ maxWidth: 820, mx: 'auto', mt: 4, mb: 6 }}>
      <style>{`@media print { .no-print { display:none !important; } body { background:#fff !important; } .is-card { box-shadow:none !important; } }`}</style>
      <Box display="flex" gap={1.5} mb={2} className="no-print" alignItems="center" flexWrap="wrap">
        <TextField type="date" label="From" value={from} size="small"
          onChange={e => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField type="date" label="To" value={to} size="small"
          onChange={e => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
        <IconButton onClick={run} disabled={loading} color="primary" sx={{ bgcolor: '#2e7d3215' }}>
          {loading ? <CircularProgress size={20} /> : <Assessment />}
        </IconButton>
        <Box flex={1} />
        <IconButton onClick={exportExcel} title="Export to Excel" disabled={!data}
          sx={{ bgcolor: 'background.paper', color: 'success.main', '&:hover': { bgcolor: 'grey.100' } }}><Description /></IconButton>
        <IconButton onClick={() => window.print()} title="Print" disabled={!data}
          sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}><Print /></IconButton>
        <IconButton onClick={() => navigate('/finance')} title="Finance Home"
          sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}><Home /></IconButton>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card elevation={3} className="is-card">
        <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
          <CompanyHeader title="INCOME STATEMENT" subtitle={`${from} → ${to}`} />
          {data && (
            <Table size="small">
              <TableBody>
                <Section title="Income" rows={data.revenue} total={data.totals.income} color="#2e7d32" />
                <Section title="Expenses" rows={data.expense} total={data.totals.expense} color="#c62828" />
                <TableRow><TableCell colSpan={2}><Divider sx={{ my: 1 }} /></TableCell></TableRow>
                <TableRow sx={{ bgcolor: profit ? 'success.50' : 'error.50' }}>
                  <TableCell sx={{ fontWeight: 900, fontSize: '1rem' }}>
                    {profit ? 'NET PROFIT' : 'NET LOSS'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 900, fontSize: '1rem', color: profit ? 'success.main' : 'error.main' }}>
                    {sym()} {fmt(Math.abs(data.totals.netProfit))}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default IncomeStatement;
